import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface AdminLayoutProps {
  children: React.ReactNode;
  activeSection?: string;
  onSectionChange?: (section: string) => void;
}

export default function AdminLayout({ children, activeSection = '', onSectionChange }: AdminLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();

  const menuItems = [
    { id: 'hero', label: 'Hero', icon: 'ri-home-line' },
    { id: 'services', label: 'Servicios', icon: 'ri-service-line' },
    { id: 'portfolio', label: 'Portafolio', icon: 'ri-briefcase-line' },
    { id: 'about', label: 'Quiénes Somos', icon: 'ri-information-line' },
    { id: 'contact', label: 'Contacto', icon: 'ri-phone-line' },
    { id: 'testimonials', label: 'Testimonios', icon: 'ri-chat-quote-line' },
    { id: 'footer', label: 'Footer', icon: 'ri-layout-bottom-line' },
    { id: 'messages', label: 'Message Center', icon: 'ri-mail-line' },
    { id: 'settings', label: 'Configuración API', icon: 'ri-settings-3-line' },
  ];

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const handleMenuClick = (itemId: string) => {
    if (itemId === 'messages') {
      navigate('/admin/messages');
    } else if (itemId === 'settings') {
      navigate('/admin/message-center-settings');
    } else {
      navigate('/admin/dashboard');
      if (onSectionChange) {
        onSectionChange(itemId);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside
        className={`bg-gradient-to-b from-gray-900 to-gray-800 text-white transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-20'
        } flex flex-col`}
      >
        <div className="p-4 flex items-center justify-between border-b border-gray-700">
          {sidebarOpen && (
            <div>
              <h2 className="text-xl font-bold">Axloweb</h2>
              <p className="text-xs text-gray-400">Admin Panel</p>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
          >
            <i className={`ri-${sidebarOpen ? 'menu-fold' : 'menu-unfold'}-line text-xl`}></i>
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleMenuClick(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 cursor-pointer ${
                activeSection === item.id
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'hover:bg-gray-700 text-gray-300'
              }`}
            >
              <i className={`${item.icon} text-xl`}></i>
              {sidebarOpen && <span className="font-medium">{item.label}</span>}
            </button>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto flex flex-col">
        {/* Top Bar */}
        <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-end">
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-logout-box-line text-lg w-5 h-5 flex items-center justify-center"></i>
            <span className="font-medium">Cerrar Sesión</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 max-w-7xl w-full mx-auto p-8">{children}</div>
      </main>
    </div>
  );
}
